import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';

import {
  useWorkspaceFileExistence,
  useWorkspaceFilePreviewLink,
} from '@/features/workspace-files';
import { findWorkspacePaths } from '@/shared/components/markdownFilePaths';
import type { MarkdownFilePaths } from '@/shared/components/Markdown';
import type { TaskView } from '@/lib/api/generated/model';
import { hasNonTerminalActivity } from '../executionStatus';
import { stepDisplayName } from '../stepNames';
import { artifactsReadOf } from '../drawerTabs';
import { useTaskArtifacts } from '../hooks/useTaskArtifacts';
import { useTaskResults } from '../hooks/useTaskResults';
import { forgeLinks } from '../forgeLinks';
import { resolveRunPath } from '../runFilePaths';
import { runLabelIndex } from '../runLabel';
import { AlsoChangedFiles } from './AlsoChangedFiles';
import { CreatedByTask } from './CreatedByTask';
import { ResultFiles } from './ResultFiles';
import { ResultRunGroup } from './ResultRunGroup';
import type { RunArtifactLine } from './RunArtifactLines';
import { ResultsPlaceholder } from './ResultsPlaceholder';

export interface TaskResultsPanelProps {
  workspaceId: string;
  taskId: string;
  task: TaskView;
  /**
   * The board's current step labels, keyed by step id. A run names its step
   * by id; this is how the reader gets the word instead (`stepDisplayName`).
   */
  stepLabels?: ReadonlyMap<string, string>;
  /** The file already previewed beside this task: a further open is a replace. */
  previewPath?: string;
}

/**
 * The Results tab: everything a task produced, per run newest first, and the
 * file changes nobody attributed to a run at the end (spec 095 FR-008, FR-016).
 *
 * The payload arrives newest first and is shown in that order; the panel does
 * not re-sort what the service already ordered. Files are partitioned by the
 * run they belong to, and a change with no run — a capture the service could
 * not tie to an execution — is still the task's, so it is listed under its own
 * heading rather than dropped.
 *
 * Reads the same query the drawer opened with. The drawer is the polling
 * observer (see `useTaskResults`); this panel subscribes with `poll: false`
 * so two observers never mean two intervals.
 */
export function TaskResultsPanel({
  workspaceId,
  taskId,
  task,
  stepLabels,
  previewPath,
}: TaskResultsPanelProps) {
  // Where a result's file path leads is decided once, app-wide, by the shared
  // hook — not here (spec 140 FR-050).
  const previewLink = useWorkspaceFilePreviewLink(workspaceId, previewPath);
  const live = hasNonTerminalActivity([task]);
  const { results, isPending, loadFailed, retry } = useTaskResults(
    workspaceId,
    taskId,
    { enabled: true, poll: false, live },
  );
  // What the task created (spec 119): its own read, its own states, so a
  // failure here never costs the runs below (FR-015).
  const artifactsQuery = useTaskArtifacts(workspaceId, taskId, {
    enabled: true,
    poll: false,
    live,
  });
  const artifactsRead = artifactsReadOf(artifactsQuery);
  const runLabels = useMemo(
    () => runLabelIndex(task, stepLabels),
    [task, stepLabels],
  );
  // Each run's references, resolved against the list the section shows. A
  // reference the list does not (yet) hold is left out rather than drawn
  // broken — the two reads were taken a moment apart (spec 119 edge cases).
  const artifactsById = useMemo(
    () =>
      new Map(
        (artifactsQuery.artifacts?.artifacts ?? []).map((artifact) => [
          artifact.id,
          artifact,
        ]),
      ),
    [artifactsQuery.artifacts],
  );
  // Delayed, so a fast answer shows no placeholder at all (spec 088's rule,
  // FR-025); shaped like a run group when it does show.
  const showPlaceholder = usePendingDisplay(isPending);

  const executions = useMemo(() => results?.executions ?? [], [results]);
  const files = useMemo(() => results?.files ?? [], [results]);

  // A path a summary mentions means the file that run wrote, when the run
  // recorded one by that name — "main.py" for a file it saved in its task
  // folder — and only otherwise the path as written, from the root.
  const resolverOf = useMemo(() => {
    const byRun = new Map<string, typeof files>();
    for (const file of files) {
      if (file.execution_id === null) continue;
      byRun.set(file.execution_id, [
        ...(byRun.get(file.execution_id) ?? []),
        file,
      ]);
    }
    return (executionId: string) => (path: string) =>
      resolveRunPath(path, byRun.get(executionId) ?? []);
  }, [files]);

  // Paths the summaries mention, checked against the workspace once for all
  // groups (FR-018): one listing per folder, shared with the file browser.
  const mentioned = useMemo(
    () => [
      ...new Set(
        executions.flatMap((run) =>
          run.result
            ? findWorkspacePaths(run.result.summary).map(
                resolverOf(run.execution_id),
              )
            : [],
        ),
      ),
    ],
    [executions, resolverOf],
  );
  const existence = useWorkspaceFileExistence(workspaceId, mentioned);
  const filePathsOf = (executionId: string): MarkdownFilePaths => {
    const resolve = resolverOf(executionId);
    return {
      exists: (path) => existence.exists(resolve(path)),
      // The per-execution `resolve` still runs first and still runs here
      // (FR-039): a summary names a path relative to its run, and only this
      // component knows which run it is reading. The shared hook receives the
      // path already resolved — all it decides is the destination.
      linkTo: (path) => previewLink(resolve(path)),
    };
  };

  if (isPending) {
    return showPlaceholder ? <ResultsPlaceholder /> : null;
  }

  if (loadFailed || !results) {
    // Stated here and nowhere else (FR-026): the tab strip, the header and
    // the board behind the drawer are not part of what failed.
    return (
      <div
        className="flex flex-col items-start gap-2"
        data-testid="results-state"
      >
        <p className="text-muted-foreground text-sm">
          This task&rsquo;s results could not be loaded.
        </p>
        <Button variant="outline" size="sm" onClick={retry}>
          Try again
        </Button>
      </div>
    );
  }

  const unattributed = files.filter((file) => file.execution_id === null);
  const producedNothing =
    executions.every((run) => run.result === null) &&
    files.length === 0 &&
    // Not while the artifact read is still out: "nothing yet" and then a
    // merge request appearing under it would be the empty state lying.
    artifactsRead !== 'pending' &&
    artifactsById.size === 0;

  if (producedNothing) {
    // Reachable only when an earlier read offered the tab and a later one
    // came back empty (FR-027). Distinguishable from a failure: no retry.
    return (
      <p className="text-muted-foreground text-sm" data-testid="results-state">
        This task has not produced anything yet.
      </p>
    );
  }

  // A file change recorded the step's name at the time, which names a step the
  // board no longer has.
  const recordedNameOf = (stepId: string) =>
    files.find((file) => file.step_id === stepId && file.step_name)
      ?.step_name ?? null;
  const links = forgeLinks([...artifactsById.values()]);
  const linesOf = (execution: (typeof executions)[number]) =>
    (execution.artifacts ?? []).flatMap((ref): RunArtifactLine[] => {
      const artifact = artifactsById.get(ref.artifact_id);
      return artifact ? [{ role: ref.role, artifact }] : [];
    });
  /*
    A step the task only passed through (Backlog, a gate someone answered,
    Done) produced nothing to show, and each such pass printed "Skipped · No
    summary recorded for this run." between the runs that did (spec 181). They
    are named once, at the end, as the Runs tab names them.
  */
  const producedSomething = (execution: (typeof executions)[number]) =>
    execution.status !== 'skipped' ||
    execution.result !== null ||
    (execution.artifacts ?? []).length > 0 ||
    files.some((file) => file.execution_id === execution.execution_id);
  const shown = executions.filter(producedSomething);
  const passedThrough = [
    ...new Set(
      executions
        .filter((execution) => !producedSomething(execution))
        .map((execution) =>
          stepDisplayName(
            execution.step_id,
            stepLabels,
            recordedNameOf(execution.step_id),
          ),
        ),
    ),
  ];
  return (
    <div className="flex flex-col gap-6">
      <CreatedByTask
        read={artifactsRead}
        retry={artifactsQuery.retry}
        runLabels={runLabels}
      />
      <ol className="flex flex-col">
        {shown.map((execution, index) => (
          <ResultRunGroup
            key={execution.execution_id}
            workspaceId={workspaceId}
            execution={execution}
            name={stepDisplayName(
              execution.step_id,
              stepLabels,
              recordedNameOf(execution.step_id),
            )}
            files={files.filter(
              (file) => file.execution_id === execution.execution_id,
            )}
            filePaths={filePathsOf(execution.execution_id)}
            artifactLines={linesOf(execution)}
            forgeLinks={links}
            artifactRecordIncomplete={
              execution.artifact_record_incomplete ?? false
            }
            isLast={index === shown.length - 1}
          />
        ))}
      </ol>
      {passedThrough.length > 0 ? (
        <p className="text-muted-foreground text-xs">
          Passed without a run: {passedThrough.join(', ')}
        </p>
      ) : null}
      {unattributed.length > 0 ? (
        <section
          aria-labelledby="task-results-other-changes"
          className="flex flex-col gap-3"
        >
          <h3
            id="task-results-other-changes"
            className="text-foreground text-sm font-medium"
          >
            Other changes
          </h3>
          <ResultFiles workspaceId={workspaceId} files={unattributed} />
        </section>
      ) : null}
      <AlsoChangedFiles
        workspaceId={workspaceId}
        files={results?.also_changed ?? []}
      />
    </div>
  );
}
